import { motion } from 'motion/react';
import { QRCodeSVG } from 'qrcode.react';
import { StampCell } from './stamp-icons';
import { stampsToNextCard, stampsPerCard } from './bit-brew-constants';
import { buildMemberPassCode } from '../../services/member-pass-code';
import PixelLogo from '../shared/PixelLogo';

interface PixelPassProps {
  purchases: number;
  stampIcon?: string;          // 'default' | preset id | 'logo'
  shopPixelImageUrl?: string;  // used when stampIcon === 'logo'
  shopName?: string;           // pass header mark + logo alt text
  streakWeeks?: number;        // consecutive weeks with >=1 purchase at this shop
  accentColor?: string | null; // shop's storefront tint (040); null = cyan
  streakRepairable?: boolean;  // streak saver (041): last week is buy-back-able
  streakRepairCost?: number;
  canAffordRepair?: boolean;
  currencyLabel?: string;
  onRepairStreak?: () => void;
  customerAddress?: string;    // member QR: the customer's zkLogin address
  /** On-chain card size mirror. Undefined/null = the 9+1 default. */
  freeEveryN?: number | null;
  shopId?: string;             // member QR: the shop's SUPABASE uuid
  embedded?: boolean;          // kept for prop-surface stability — unused, same as the other four PASS-tab components
}

export default function PixelPass({
  purchases,
  stampIcon = 'default',
  shopPixelImageUrl,
  shopName,
  streakWeeks,
  accentColor,
  streakRepairable,
  streakRepairCost,
  canAffordRepair,
  currencyLabel,
  onRepairStreak,
  customerAddress,
  shopId,
  freeEveryN,
}: PixelPassProps) {
  // Card length is per-shop (set_card_size, mirrored by 061). perCard is the
  // EARNED count; the grid still draws perCard + 1 tiles because the last is
  // the free redemption, not a purchase.
  const perCard = stampsPerCard({ free_every_n: freeEveryN });
  const gridSize = perCard + 1;
  const filledSlots = purchases % perCard;
  const completedCards = Math.floor(purchases / perCard);
  const toNextItemBox = stampsToNextCard(purchases, perCard);

  // null when either input is missing or malformed — see the QR block below.
  const memberCode = buildMemberPassCode(customerAddress, shopId);

  return (
    // 2px ink trim, same wrapper technique as TabBar/ArcadeCard — a CSS border
    // cannot follow a clip-path shape.
    <div className="pixel-corners bg-primary p-[2px]">
    <div className="bg-white pixel-corners p-[16px] flex flex-col justify-between h-full">
      {/* Always renders its own section label now — same always-on-label
          treatment as LootBag/BadgeStrip/QuestCarousel/LoreArchive, since
          nothing external supplies one after Task 6 removed the popups. */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-[9px] tracking-[1px] uppercase text-body-grey">
          PIXEL PASS
        </h2>
        {shopName && (
          <PixelLogo shopName={shopName} pixelImageUrl={shopPixelImageUrl} size={24} glowColor={accentColor || undefined} />
        )}
      </div>

      {/* 5x2 stamp grid — pixel-corners-sm clips the border at the corners,
          the same notched frame the member-QR container below gets (Nik's
          on-device call 2026-08-06: cells should match the QR border). */}
      <div className="grid grid-cols-5 gap-2 mb-4">
        {Array.from({ length: gridSize }, (_, i) => {
          // The last tile is never earnable — it is filled by the
          // redemption. Showing it as FREE from the first visit is what
          // makes "buy 9, your 10th is free" legible without a caption.
          if (i === perCard) {
            return (
              <div
                key={i}
                className="aspect-square pixel-corners-sm border-[3px] border-coral bg-coral/10 flex items-center justify-center"
              >
                <span className="font-heading text-[7px] text-coral leading-none">FREE</span>
              </div>
            );
          }
          return (
            <motion.div
              key={i}
              initial={i === filledSlots - 1 && filledSlots > 0 ? { scale: 0 } : false}
              animate={{ scale: 1 }}
              className={`aspect-square pixel-corners-sm border-[3px] border-primary overflow-hidden ${
                i < filledSlots ? '' : 'bg-background'
              }`}
            >
              {i < filledSlots && (
                <StampCell
                  icon={stampIcon}
                  accentColor={accentColor}
                  shopPixelImageUrl={shopPixelImageUrl}
                  shopName={shopName}
                />
              )}
            </motion.div>
          );
        })}
      </div>

      <div className="flex justify-between items-center mb-2 flex-wrap gap-1">
        <span className="font-heading text-[9px] text-coral">
          {filledSlots} / {perCard} STAMPS · {gridSize}TH FREE
        </span>
        {completedCards > 0 && (
          <span className="font-heading text-[9px] text-body-grey">
            {completedCards} CARD{completedCards > 1 ? 'S' : ''} DONE
          </span>
        )}
      </div>

      {(streakWeeks ?? 0) >= 2 && (
        <p className="font-grotesk font-medium text-[13px] text-body-grey mb-2">🔥 {streakWeeks}-week streak</p>
      )}

      {streakRepairable && onRepairStreak && (
        <button
          onClick={onRepairStreak}
          disabled={!canAffordRepair}
          className="w-full pixel-corners-sm bg-tab-inactive text-body-grey font-grotesk font-bold text-[13px] py-3 mb-2 disabled:opacity-40"
          title={canAffordRepair ? undefined : `Not enough ${currencyLabel || 'BEANS'}`}
        >
          🔥 Streak broke last week — save it for {streakRepairCost ?? 50} {currencyLabel || 'beans'}
        </button>
      )}

      {/* Counts to the next CARD completion, which mints an item box since
          contract v4's award_card_completion. It previously counted to the
          next LEVEL threshold, because level-up was the only thing that
          minted one and a card countdown promised rewards that never came. */}
      <p className="font-heading text-[9px] text-coral">
        {`${toNextItemBox} STAMP${toNextItemBox === 1 ? '' : 'S'} TO NEXT ITEM BOX`}
      </p>

      {/* ── Member QR ────────────────────────────────────────────────────────
          The thing the barista scans. It lives HERE, on the card, unconditional
          and zero-tap, because until 2026-08-02 this code existed only inside
          an Apple/Google wallet pass — so a customer had to have selected a
          shop, been on the right platform, tapped "Add to Wallet" at some
          earlier point, then left the app to show it. Anyone who had not done
          all four had nothing to show while the counter UI said SCAN MEMBER
          PASS. A loyalty card whose barcode is three navigations away is not a
          loyalty card.

          Rendered null-safe: buildMemberPassCode returns null rather than a
          malformed string, because the scanner ignores unparseable payloads
          SILENTLY — a wrong QR would look like a broken camera, not an error.

          White quiet zone is not decoration: scanners need the light margin
          regardless of what the card itself sits on. */}
      {memberCode && (
        <div className="mt-4 pt-4 border-t-2 border-hairline flex flex-col items-center">
          <div className="bg-white p-2 pixel-corners border-[3px] border-primary" aria-label="Your member code">
            {/* Same options as the printed lite cards in MemberCards.tsx —
                explicit black-on-white and includeMargin. The card's own
                accentColor must never reach the QR: a tinted or low-contrast
                code is the classic way to make one that looks fine and scans
                badly under café lighting. */}
            <QRCodeSVG
              value={memberCode}
              size={124}
              level="M"
              bgColor="#ffffff"
              fgColor="#000000"
              includeMargin
            />
          </div>
          <p className="font-grotesk font-medium text-[13px] text-body-grey mt-2 text-center">
            Show this to staff to get stamped.
          </p>
        </div>
      )}
    </div>
    </div>
  );
}
